import React, { useState } from 'react';
import { Building2, Phone, Mail, Upload, ArrowRight } from 'lucide-react';
import './VendorStyles.css';

export default function VendorProfileForm({ onSubmit, initialData }) {
  // Use initialData to populate fields if editing, else empty strings
  const [formData, setFormData] = useState({
    vendorId: initialData?.vendorId || `V-${Date.now().toString().slice(-6)}`,
    vendorName: initialData?.vendorName || '',
    
    // Address
    street: initialData?.street || '',
    city: initialData?.city || '',
    state: initialData?.state || '',
    pincode: initialData?.pincode || '',
    country: initialData?.country || '',

    // Contacts
    vendorPhone: initialData?.vendorPhone || '',
    vendorEmail: initialData?.vendorEmail || '',

    // Bank details
    bankName: initialData?.bankName || '',
    accountNumber: initialData?.accountNumber || '',
    accountHolderName: initialData?.accountHolderName || '',
    ifscCode: initialData?.ifscCode || '',
    swiftCode: initialData?.swiftCode || '',
    branchName: initialData?.branchName || '',
    branchAddress: initialData?.branchAddress || '',

    // Numbers
    panNo: initialData?.panNo || '',
    gstNo: initialData?.gstNo || '',
    msmeNo: initialData?.msmeNo || ''
  });

  // File States
  const [chequeFile, setChequeFile] = useState(null);
  const [panFile, setPanFile] = useState(null);
  const [gstFile, setGstFile] = useState(null);
  const [msmeFile, setMsmeFile] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e, setFile) => {
    if (e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // --- CRITICAL CHANGE: Use FormData for File Uploads ---
    // Standard JSON cannot send files. We must use FormData.
    const data = new FormData();

    // 1. Append all text fields
    Object.keys(formData).forEach(key => {
        data.append(key, formData[key]);
    });

    // 2. Append Files (Only if the user selected a new one)
    if (chequeFile) data.append('chequeFile', chequeFile);
    if (panFile) data.append('panFile', panFile);
    if (gstFile) data.append('gstFile', gstFile);
    if (msmeFile) data.append('msmeFile', msmeFile);

    // 3. Send the FormData object to the parent component
    onSubmit(data);
  };

  return (
    <div className="vendor-onboarding-container">
      <div className="onboarding-card">
        
        <div className="onboarding-header">
          <div className="icon-badge-lg">
            <Building2 size={32} color="#059669" />
          </div>
          <h1>{initialData ? 'Edit Vendor Profile' : 'Vendor Registration'}</h1>
          <p>Complete your profile to start bidding.</p>
        </div>

        <form className="onboarding-form" onSubmit={handleSubmit}>
          
          {/* 1. Basic Details */}
          <h4 className="section-label">Organization Details</h4>
          <div className="form-group">
            <label>Vendor ID (Auto-Generated)</label>
            <input type="text" value={formData.vendorId} disabled style={{ background: '#f1f5f9', color: '#64748b' }} />
          </div>
          <div className="form-group">
            <label>Vendor / Organization Name *</label>
            <input type="text" name="vendorName" value={formData.vendorName} onChange={handleChange} required />
          </div>

          {/* 2. Contact Info */}
          <h4 className="section-label">Contact Information</h4>
          <div className="form-row">
            <div className="form-group">
              <label>Phone Number *</label>
              <div className="input-wrapper">
                <Phone size={16} className="input-icon" />
                <input type="tel" name="vendorPhone" value={formData.vendorPhone} onChange={handleChange} required />
              </div>
            </div>
            <div className="form-group">
              <label>Email Address *</label>
              <div className="input-wrapper">
                <Mail size={16} className="input-icon" />
                <input type="email" name="vendorEmail" value={formData.vendorEmail} onChange={handleChange} required />
              </div>
            </div>
          </div>

          {/* 3. Address */}
          <h4 className="section-label">Registered Address</h4>
          <div className="form-group">
            <label>Street Address *</label>
            <input type="text" name="street" value={formData.street} onChange={handleChange} required />
          </div>
          <div className="form-row three-col">
            <div className="form-group"><label>City *</label><input type="text" name="city" value={formData.city} onChange={handleChange} required /></div>
            <div className="form-group"><label>State *</label><input type="text" name="state" value={formData.state} onChange={handleChange} required /></div>
            <div className="form-group"><label>Pincode *</label><input type="text" name="pincode" value={formData.pincode} onChange={handleChange} required /></div>
          </div>
          <div className="form-group"><label>Country *</label><input type="text" name="country" value={formData.country} onChange={handleChange} required /></div>

          {/* 4. Statutory Numbers */}
          <h4 className="section-label">Statutory Details</h4>
          <div className="form-row three-col">
            <div className="form-group"><label>PAN No *</label><input type="text" name="panNo" value={formData.panNo} onChange={handleChange} required /></div>
            <div className="form-group"><label>GST No *</label><input type="text" name="gstNo" value={formData.gstNo} onChange={handleChange} required /></div>
            <div className="form-group"><label>MSME No (Optional)</label><input type="text" name="msmeNo" value={formData.msmeNo} onChange={handleChange} /></div>
          </div>

          {/* 5. Bank Details */}
          <h4 className="section-label">Bank Account Details</h4>
          <div className="form-row">
            <div className="form-group"><label>Bank Name *</label><input type="text" name="bankName" value={formData.bankName} onChange={handleChange} required /></div>
            <div className="form-group"><label>Branch Name *</label><input type="text" name="branchName" value={formData.branchName} onChange={handleChange} required /></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label>Account Number *</label><input type="text" name="accountNumber" value={formData.accountNumber} onChange={handleChange} required /></div>
            <div className="form-group"><label>IFSC Code *</label><input type="text" name="ifscCode" value={formData.ifscCode} onChange={handleChange} required /></div>
          </div>
          <div className="form-group"><label>Account Holder Name *</label><input type="text" name="accountHolderName" value={formData.accountHolderName} onChange={handleChange} required /></div>
          <div className="form-row">
            <div className="form-group"><label>Swift Code (Optional)</label><input type="text" name="swiftCode" value={formData.swiftCode} onChange={handleChange} /></div>
            <div className="form-group"><label>Branch Address</label><input type="text" name="branchAddress" value={formData.branchAddress} onChange={handleChange} /></div>
          </div>

          {/* 6. Documents */}
          <h4 className="section-label">Document Uploads</h4>
          <div className="form-row">
            <div className="form-group">
              <label>Cancelled Cheque *</label>
              <div className="file-input-wrapper">
                <Upload size={16} />
                {/* Required only if new profile (no initial data) */}
                <input type="file" onChange={(e) => handleFileChange(e, setChequeFile)} required={!initialData} /> 
                <span>{chequeFile ? chequeFile.name : "Upload File"}</span>
              </div>
            </div>
            <div className="form-group">
              <label>PAN Card *</label>
              <div className="file-input-wrapper">
                <Upload size={16} />
                <input type="file" onChange={(e) => handleFileChange(e, setPanFile)} required={!initialData} />
                <span>{panFile ? panFile.name : "Upload File"}</span>
              </div>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>GST Certificate *</label>
              <div className="file-input-wrapper">
                <Upload size={16} />
                <input type="file" onChange={(e) => handleFileChange(e, setGstFile)} required={!initialData} />
                <span>{gstFile ? gstFile.name : "Upload File"}</span>
              </div>
            </div>
            <div className="form-group">
              <label>MSME Certificate (Optional)</label>
              <div className="file-input-wrapper">
                <Upload size={16} />
                <input type="file" onChange={(e) => handleFileChange(e, setMsmeFile)} />
                <span>{msmeFile ? msmeFile.name : "Upload File"}</span>
              </div>
            </div>
          </div>

          <button type="submit" className="submit-profile-btn" style={{ marginTop: '30px' }}>
            {initialData ? 'Update Profile' : 'Complete Registration'} <ArrowRight size={18} />
          </button>

        </form>
      </div>
    </div>
  );
}